// Query hooks that turn the active filters into rollup queries. Routes call
// these and stay declarative; the queryKey carries the filter values so the
// data refetches whenever a filter changes.
import { useQuery } from "@tanstack/react-query";
import { useFilters } from "@/lib/filters";
import {
  getTotals, getDailySeries, getBreakdown,
  type RollupQuery, type RollupGroup,
} from "@/lib/api/metrics";
import { getCollaborations, type CollabQuery } from "@/lib/api/collabs";
import type { ActivityFilter } from "@/lib/api/types";
import { MARKET_CURRENCY } from "@/lib/currency";

/** Build a RollupQuery from the active filters, optionally pinning the activity
 *  (dashboard pages like Ecom / Awareness fix their own activity). */
export function useRollupQuery(activityOverride?: ActivityFilter): RollupQuery {
  const f = useFilters();
  return {
    from: f.fromDate,
    to: f.toDate,
    activity: activityOverride ?? f.activity,
    region: f.region,
    brand: f.brand,
    channel: f.channel,
    currency: f.region === "all" ? f.currency : MARKET_CURRENCY[f.region],
  };
}

const key = (q: RollupQuery, ...extra: string[]) =>
  ["metrics", q.from, q.to, q.activity, q.region, q.brand, q.channel, q.currency, ...extra];

export function useTotals(activityOverride?: ActivityFilter) {
  const q = useRollupQuery(activityOverride);
  return useQuery({ queryKey: key(q, "totals"), queryFn: () => getTotals(q) });
}

export function useSeries(activityOverride?: ActivityFilter) {
  const q = useRollupQuery(activityOverride);
  return useQuery({ queryKey: key(q, "series"), queryFn: () => getDailySeries(q) });
}

export function useBreakdown(dim: Exclude<RollupGroup, "total" | "date">, activityOverride?: ActivityFilter, currency?: RollupQuery["currency"], region?: RollupQuery["region"]) {
  const q = useRollupQuery(activityOverride);
  if (currency) q.currency = currency;
  if (region) q.region = region;
  return useQuery({ queryKey: key(q, "breakdown", dim), queryFn: () => getBreakdown(q, dim) });
}

export function useCollaborations() {
  const f = useFilters();
  const q: CollabQuery = { region: f.region, brand: f.brand };
  return useQuery({
    queryKey: ["collabs", q.region, q.brand],
    queryFn: () => getCollaborations(q),
  });
}
